import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Printer, Loader2, X, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Veresiye Müşteri Ekstresi — tarih aralığında müşterinin tükettiği ürünleri
 * gün gün listeleyen, alt toplam + grand total veren, logo filigranlı estetik rapor.
 * Yazdırılabilir (window.print() via iframe) ve A4 dikey tasarımlı.
 */
const VeresiyeCustomerStatement = ({ open, onClose, customerName, customerPhone, token }) => {
  const today = new Date().toISOString().slice(0, 10);
  const sixMonthsAgo = new Date(); sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const defaultFrom = sixMonthsAgo.toISOString().slice(0, 10);

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(today);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchStatement = async () => {
    if (!customerName) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/veresiye/customer-statement`, {
        params: { customer_name: customerName, date_from: dateFrom, date_to: dateTo },
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rapor alınamadı');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch when dialog opens
  React.useEffect(() => {
    if (open && customerName) fetchStatement();
    if (!open) setData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerName]);

  const fmtTr = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' }); } catch { return iso; }
  };
  const fmtShort = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return iso; }
  };

  const handleWhatsApp = () => {
    if (!data) return;
    const phone = (customerPhone || data.customer_phone || '').replace(/[^\d+]/g, '');
    let text = `*Central Restaurant - Veresiye Ekstresi*\n`;
    text += `Müşteri: ${customerName}\n`;
    text += `Dönem: ${fmtShort(dateFrom)} - ${fmtShort(dateTo)}\n\n`;
    (data.days || []).forEach((day) => {
      text += `📅 *${fmtTr(day.date)}*\n`;
      (day.items || []).forEach((it) => {
        text += `  • ${it.name} x${it.quantity} = €${Number(it.subtotal).toFixed(2)}\n`;
      });
      text += `  Günlük Toplam: *€${day.total_amount.toFixed(2)}*\n\n`;
    });
    text += `━━━━━━━━━━━━━━━━\n`;
    text += `💰 Toplam: *€${data.grand_total.toFixed(2)}*\n`;
    text += `✅ Dönem Ödenen: €${data.total_paid_in_period.toFixed(2)}\n`;
    text += `⚠️ Genel Bakiye: *€${(data.current_balance ?? 0).toFixed(2)}*\n\n`;
    text += `Central Hotel & Restaurant\ncentralhotel.org`;
    const encoded = encodeURIComponent(text);
    const url = phone
      ? `https://wa.me/${phone}?text=${encoded}`
      : `https://wa.me/?text=${encoded}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handlePrint = () => {
    const node = document.getElementById('veresiye-statement-printable');
    if (!node) return;
    const html = node.innerHTML;
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) { toast.error('Pop-up engellendi'); return; }
    w.document.write(`<!doctype html><html><head><title>Veresiye Ekstresi</title>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Manrope:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        *{box-sizing:border-box} html,body{margin:0;padding:0;font-family:'Manrope',sans-serif;color:#2C2420;background:#fff;}
        .a4{width:210mm;max-width:100%;margin:0 auto;padding:12mm 12mm;position:relative;}
        .watermark{position:absolute;top:50%;left:50%;width:260px;height:260px;margin:-130px 0 0 -130px;transform:rotate(-22deg);opacity:0.045;pointer-events:none;}
        .watermark img{width:100%;height:100%;object-fit:contain;}
        .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #BC5229;padding-bottom:12px;margin-bottom:14px;}
        .brand{display:flex;gap:12px;align-items:center;}
        .brand img{width:54px;height:54px;border-radius:12px;object-fit:cover;box-shadow:0 4px 12px rgba(0,0,0,.08);}
        .brand h1{font-family:'Playfair Display',serif;font-size:20px;margin:0;color:#2C2420;}
        .brand p{margin:2px 0 0;font-size:9px;color:#888;letter-spacing:.16em;text-transform:uppercase;}
        .docmeta{text-align:right;font-size:10px;color:#666;}
        .docmeta strong{display:block;font-size:16px;color:#BC5229;font-family:'Playfair Display',serif;letter-spacing:.05em;margin-bottom:2px;}
        .cust{display:grid;grid-template-columns:1fr 1fr;gap:6px 20px;background:#FAF6EF;border-left:4px solid #BC5229;padding:12px 16px;border-radius:8px;margin-bottom:14px;}
        .cust .lbl{font-size:9px;color:#888;text-transform:uppercase;letter-spacing:.12em;}
        .cust .val{font-size:13px;font-weight:600;color:#2C2420;}
        .day{margin-bottom:12px;border:1px solid #ece6dc;border-radius:10px;overflow:hidden;page-break-inside:avoid;}
        .day-h{background:linear-gradient(135deg,#2C2420 0%,#3a302a 100%);color:#fff;padding:7px 12px;display:flex;justify-content:space-between;align-items:center;}
        .day-h .d{font-family:'Playfair Display',serif;font-size:13px;font-weight:600;}
        .day-h .t{font-weight:700;font-size:13px;color:#FFE5C9;}
        table{width:100%;border-collapse:collapse;font-size:11px;table-layout:fixed;}
        th{background:#F9F7F2;text-align:left;padding:6px 10px;font-weight:600;color:#666;font-size:9px;text-transform:uppercase;letter-spacing:.06em;}
        td{padding:6px 10px;border-bottom:1px solid #F0EBE3;word-break:break-word;}
        tr:last-child td{border-bottom:none;}
        td.qty,td.unit,td.amt,th.qty,th.unit,th.amt{text-align:right;white-space:nowrap;}
        col.c-qty{width:60px;}
        col.c-unit{width:80px;}
        col.c-amt{width:90px;}
        .sub{background:#FFF5EB;font-weight:600;color:#BC5229;}
        .summary{margin-top:14px;background:linear-gradient(135deg,#BC5229 0%,#a04421 100%);color:#fff;border-radius:12px;padding:14px 18px;display:grid;grid-template-columns:repeat(3,1fr);gap:12px;page-break-inside:avoid;}
        .summary .b{font-size:9px;text-transform:uppercase;letter-spacing:.14em;opacity:.85;}
        .summary .v{font-family:'Playfair Display',serif;font-size:18px;font-weight:700;margin-top:3px;}
        .pay{margin-top:14px;padding:12px;border:1px dashed #BC5229;border-radius:10px;background:#FFF8F0;page-break-inside:avoid;}
        .pay h3{font-family:'Playfair Display',serif;margin:0 0 6px;color:#BC5229;font-size:13px;}
        .footer{margin-top:20px;font-size:9px;color:#888;text-align:center;border-top:1px solid #ece6dc;padding-top:10px;}
        @media print {
          html,body{width:210mm;}
          .a4{padding:10mm 10mm;}
          @page{size:A4;margin:0;}
        }
      </style></head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 600);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="veresiye-statement-dialog">
        {/* Toolbar */}
        <div className="sticky top-0 bg-white z-10 px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-4 h-4 text-amber-700" />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 text-sm" data-testid="statement-date-from" />
            <span className="text-charcoal/50">→</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 text-sm" data-testid="statement-date-to" />
            <Button onClick={fetchStatement} disabled={loading} size="sm" className="bg-amber-700 text-white hover:bg-amber-600 rounded-full" data-testid="statement-filter-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Filtrele'}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleWhatsApp} disabled={!data || loading} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="statement-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp Gönder
            </Button>
            <Button onClick={handlePrint} disabled={!data || loading} size="sm" variant="outline" className="rounded-full" data-testid="statement-print-btn">
              <Printer className="w-4 h-4 mr-1.5" /> Yazdır / PDF
            </Button>
            <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded-full" data-testid="statement-close-btn"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Printable area */}
        <style>{`
          #veresiye-statement-printable .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #BC5229;padding-bottom:14px;margin-bottom:18px;}
          #veresiye-statement-printable .brand{display:flex;gap:14px;align-items:center;}
          #veresiye-statement-printable .brand img{width:60px;height:60px;border-radius:14px;object-fit:cover;box-shadow:0 4px 12px rgba(0,0,0,.08);}
          #veresiye-statement-printable .brand h1{font-family:'Playfair Display',serif;font-size:24px;margin:0;color:#2C2420;}
          #veresiye-statement-printable .brand p{margin:3px 0 0;font-size:11px;color:#888;letter-spacing:.18em;text-transform:uppercase;}
          #veresiye-statement-printable .docmeta{text-align:right;font-size:11px;color:#666;}
          #veresiye-statement-printable .docmeta strong{display:block;font-size:18px;color:#BC5229;font-family:'Playfair Display',serif;letter-spacing:.05em;margin-bottom:2px;}
          #veresiye-statement-printable .cust{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;background:#FAF6EF;border-left:4px solid #BC5229;padding:14px 18px;border-radius:8px;margin-bottom:18px;}
          #veresiye-statement-printable .cust .lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.15em;}
          #veresiye-statement-printable .cust .val{font-size:14px;font-weight:600;color:#2C2420;}
          #veresiye-statement-printable .day{margin-bottom:14px;border:1px solid #ece6dc;border-radius:10px;overflow:hidden;}
          #veresiye-statement-printable .day-h{background:linear-gradient(135deg,#2C2420 0%,#3a302a 100%);color:#fff;padding:8px 14px;display:flex;justify-content:space-between;align-items:center;}
          #veresiye-statement-printable .day-h .d{font-family:'Playfair Display',serif;font-size:15px;font-weight:600;}
          #veresiye-statement-printable .day-h .t{font-weight:700;font-size:15px;color:#FFE5C9;}
          #veresiye-statement-printable table{width:100%;border-collapse:collapse;font-size:12px;}
          #veresiye-statement-printable th{background:#F9F7F2;text-align:left;padding:6px 12px;font-weight:600;color:#666;font-size:10px;text-transform:uppercase;letter-spacing:.08em;}
          #veresiye-statement-printable td{padding:7px 12px;border-bottom:1px solid #F0EBE3;}
          #veresiye-statement-printable tr:last-child td{border-bottom:none;}
          #veresiye-statement-printable td.qty,#veresiye-statement-printable td.unit,#veresiye-statement-printable td.amt,#veresiye-statement-printable th.qty,#veresiye-statement-printable th.unit,#veresiye-statement-printable th.amt{text-align:right;white-space:nowrap;}
          #veresiye-statement-printable .sub{background:#FFF5EB;font-weight:600;color:#BC5229;}
          #veresiye-statement-printable .summary{margin-top:24px;background:linear-gradient(135deg,#BC5229 0%,#a04421 100%);color:#fff;border-radius:14px;padding:18px 24px;display:grid;grid-template-columns:repeat(3,1fr);gap:16px;}
          #veresiye-statement-printable .summary .b{font-size:10px;text-transform:uppercase;letter-spacing:.16em;opacity:.85;}
          #veresiye-statement-printable .summary .v{font-family:'Playfair Display',serif;font-size:20px;font-weight:700;margin-top:4px;}
          #veresiye-statement-printable .pay{margin-top:18px;padding:14px;border:1px dashed #BC5229;border-radius:10px;background:#FFF8F0;}
          #veresiye-statement-printable .pay h3{font-family:'Playfair Display',serif;margin:0 0 8px;color:#BC5229;font-size:14px;}
          #veresiye-statement-printable .footer{margin-top:30px;font-size:10px;color:#888;text-align:center;border-top:1px solid #ece6dc;padding-top:12px;}
        `}</style>
        <div id="veresiye-statement-printable" className="bg-white">
          <div className="a4 relative" style={{ width: '210mm', maxWidth: '100%', minHeight: 'auto', padding: '18mm 16mm', position: 'relative', overflow: 'hidden' }}>
            <div className="watermark" style={{ position: 'absolute', top: '50%', left: '50%', width: 280, height: 280, marginTop: -140, marginLeft: -140, transform: 'rotate(-22deg)', opacity: 0.045, pointerEvents: 'none' }}>
              <img src="/logo.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>

            {/* Header */}
            <div className="head">
              <div className="brand">
                <img src="/logo.png" alt="logo" />
                <div>
                  <h1>Central Hotel & Restaurant</h1>
                  <p>Akdeniz Lezzetleri • Sıcak Servis</p>
                </div>
              </div>
              <div className="docmeta">
                <strong>VERESİYE EKSTRESİ</strong>
                Tarih: <b>{fmtShort(dateFrom)}</b> – <b>{fmtShort(dateTo)}</b><br />
                Doküman: {new Date().toLocaleString('tr-TR')}
              </div>
            </div>

            {/* Customer block */}
            <div className="cust">
              <div>
                <div className="lbl">Müşteri</div>
                <div className="val" data-testid="statement-customer-name">{customerName}</div>
              </div>
              <div>
                <div className="lbl">Telefon</div>
                <div className="val">{customerPhone || data?.customer_phone || '-'}</div>
              </div>
              <div>
                <div className="lbl">Bu Dönem Kayıt</div>
                <div className="val">{data?.entry_count ?? 0}</div>
              </div>
              <div>
                <div className="lbl">Genel Bakiye</div>
                <div className="val" style={{ color: (data?.current_balance || 0) > 0 ? '#BC5229' : '#2D6A4F' }}>
                  €{(data?.current_balance ?? 0).toFixed(2)}
                </div>
              </div>
            </div>

            {/* Days */}
            {loading ? (
              <div className="text-center py-16 text-charcoal/50">Yükleniyor...</div>
            ) : !data || data.days?.length === 0 ? (
              <div className="text-center py-16 text-charcoal/50" data-testid="statement-empty">Bu tarih aralığında kayıt bulunamadı</div>
            ) : (
              <>
                {data.days.map((day) => (
                  <div key={day.date} className="day" data-testid={`statement-day-${day.date}`}>
                    <div className="day-h">
                      <div className="d">{fmtTr(day.date)}</div>
                      <div className="t">€{day.total_amount.toFixed(2)}</div>
                    </div>
                    <table>
                      <colgroup>
                        <col />
                        <col className="c-qty" style={{ width: 60 }} />
                        <col className="c-unit" style={{ width: 80 }} />
                        <col className="c-amt" style={{ width: 90 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Ürün</th>
                          <th className="qty">Miktar</th>
                          <th className="unit">Birim</th>
                          <th className="amt">Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {day.items.map((it, i) => (
                          <tr key={i}>
                            <td>{it.name}</td>
                            <td className="qty">{it.quantity}</td>
                            <td className="unit">€{Number(it.price).toFixed(2)}</td>
                            <td className="amt">€{Number(it.subtotal).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="sub">
                          <td colSpan={3} style={{ textAlign: 'right' }}>Günlük Ara Toplam</td>
                          <td className="amt">€{day.subtotal.toFixed(2)}</td>
                        </tr>
                        {/* discount row removed by user request */}
                        <tr className="sub" style={{ background: '#FFE5C9' }}>
                          <td colSpan={3} style={{ textAlign: 'right' }}>Günlük Net Toplam</td>
                          <td className="amt">€{day.total_amount.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}

                {/* Grand summary */}
                <div className="summary" data-testid="statement-grand-summary">
                  <div>
                    <div className="b">Toplam</div>
                    <div className="v">€{data.grand_total.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="b">Dönem Ödenen</div>
                    <div className="v">€{data.total_paid_in_period.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="b">Genel Bakiye</div>
                    <div className="v">€{(data.current_balance ?? 0).toFixed(2)}</div>
                  </div>
                </div>

                {/* Payments */}
                {data.payments?.length > 0 && (
                  <div className="pay">
                    <h3>Bu Dönemde Yapılan Ödemeler</h3>
                    <table>
                      <thead>
                        <tr><th>Tarih</th><th>Yöntem</th><th>Not</th><th className="amt">Tutar</th></tr>
                      </thead>
                      <tbody>
                        {data.payments.map((p, i) => (
                          <tr key={i}>
                            <td>{fmtShort(p.created_at)}</td>
                            <td>{p.method || 'Nakit'}</td>
                            <td>{p.notes || '-'}</td>
                            <td className="amt">€{Number(p.amount).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <div className="footer">
              Bu doküman Central Hotel & Restaurant veresiye sisteminden alınmıştır.
              <br />İletişim: centralhotel.org
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VeresiyeCustomerStatement;
